import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, ExternalLink, Calendar, User, FileText, Link as LinkIcon, AlertCircle, Paperclip } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TaskPeekOverview = ({ task, onClose, onNavigate }) => {
  const [detailedData, setDetailedData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (task) {
      fetchDetailedData();
    }
  }, [task]);

  const fetchDetailedData = async () => {
    try {
      setLoading(true);
      
      if (task.type === 'Project') {
        // Fetch project details for task assignments
        const response = await axios.get(`${API}/projects/${task.project_id}`);
        const project = response.data;
        
        // Find the specific task assignment
        const taskAssignment = project.task_assignments?.find(
          ta => ta.task_index === task.task_index
        );
        
        setDetailedData({
          project,
          taskAssignment
        });
      } else {
        // Fetch support ticket details
        const response = await axios.get(`${API}/support-tickets/${task.ticket_id}`);
        setDetailedData(response.data);
      }
    } catch (error) {
      console.error('Error fetching detailed data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleViewFull = () => {
    if (task.type === 'Project') {
      // Navigate to specific project task detail page
      onNavigate(`/projects/${task.project_id}/tasks/${task.task_index}`);
    } else {
      // Navigate to individual support ticket detail page
      onNavigate(`/support/tickets/${task.ticket_id}`);
    }
    onClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'urgent': 'bg-red-100 text-red-800',
      'high': 'bg-orange-100 text-orange-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'low': 'bg-green-100 text-green-800'
    };
    return colors[priority?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    const colors = {
      'To Do': 'bg-gray-100 text-gray-800',
      'Open': 'bg-blue-100 text-blue-800',
      'In Progress': 'bg-yellow-100 text-yellow-800',
      'In testing': 'bg-purple-100 text-purple-800',
      'Resolved': 'bg-green-100 text-green-800',
      'Closed': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (!task) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-30 z-50 flex justify-end"
      onClick={handleOverlayClick}
    >
      <div className="w-full max-w-2xl bg-white shadow-2xl overflow-y-auto animate-slide-in-right">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="p-6">
            {/* Header */}
            <div className="mb-6 border-b pb-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      task.type === 'Project' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                    }`}>
                      {task.type}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(task.status)}`}>
                      {task.status}
                    </span>
                    {task.priority && (
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(task.priority)}`}>
                        {task.priority}
                      </span>
                    )}
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">
                    {task.task_name}
                  </h2>
                  <p className="text-sm text-gray-500">
                    ID: {task.type === 'Project' ? `${task.project_id}_${task.task_index}` : task.ticket_id}
                  </p>
                </div>
              </div>
              
              <button
                onClick={handleViewFull}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                <ExternalLink className="w-4 h-4" />
                Open in full view
              </button>
            </div>

            {/* Project Task Content */}
            {task.type === 'Project' && detailedData && (
              <div className="space-y-6">
                {/* Task Description */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Task Description
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 text-gray-700">
                    {detailedData.taskAssignment?.task_description || 'No description provided'}
                  </div>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Start Date
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-3 text-gray-700">
                      {formatDate(detailedData.taskAssignment?.start_date)}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      End Date
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-3 text-gray-700">
                      {formatDate(detailedData.taskAssignment?.end_date)}
                    </div>
                  </div>
                </div>

                {/* Assignee */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Assigned To
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-3 text-gray-700">
                    {task.assigned_employee_name || 'Unassigned'}
                  </div>
                </div>

                {/* Task Documents */}
                {detailedData.taskAssignment?.documents && detailedData.taskAssignment.documents.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <Paperclip className="w-4 h-4" />
                      Task Documents
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      {detailedData.taskAssignment.documents.map((doc, index) => (
                        <div key={index} className="flex items-center gap-2 text-blue-600 hover:text-blue-700">
                          <FileText className="w-4 h-4" />
                          <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-sm">
                            {doc.name || `Document ${index + 1}`}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Task Links */}
                {detailedData.taskAssignment?.task_links && detailedData.taskAssignment.task_links.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <LinkIcon className="w-4 h-4" />
                      Task Links
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      {detailedData.taskAssignment.task_links.map((link, index) => (
                        <div key={index} className="flex items-center gap-2 text-blue-600 hover:text-blue-700">
                          <LinkIcon className="w-4 h-4" />
                          <a href={link} target="_blank" rel="noopener noreferrer" className="text-sm break-all">
                            {link}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Status */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Action Status
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <span className={`px-3 py-1 rounded text-sm font-medium ${getStatusColor(task.status)}`}>
                      {task.status}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Support Ticket Content */}
            {task.type === 'Support' && detailedData && (
              <div className="space-y-6">
                {/* Issue Details Section */}
                <div className="border-b pb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Issue Details</h3>
                  
                  {/* Description */}
                  <div className="mb-4">
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Description</label>
                    <div className="bg-gray-50 rounded-lg p-4 text-gray-700">
                      {detailedData.issue_description || 'No description provided'}
                    </div>
                  </div>

                  {/* Steps to Reproduce */}
                  <div className="mb-4">
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Steps to Reproduce</label>
                    <div className="bg-gray-50 rounded-lg p-4 text-gray-700">
                      {detailedData.issue_steps && detailedData.issue_steps.length > 0 ? (
                        <ol className="list-decimal list-inside space-y-1">
                          {detailedData.issue_steps.map((step, index) => (
                            <li key={index}>{step}</li>
                          ))}
                        </ol>
                      ) : (
                        'No steps provided'
                      )}
                    </div>
                  </div>

                  {/* Device/Platform */}
                  <div className="mb-4">
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Device/Platform</label>
                    <div className="bg-gray-50 rounded-lg p-3 text-gray-700">
                      {detailedData.issue_device || 'Not specified'}
                    </div>
                  </div>

                  {/* Related Link */}
                  {detailedData.related_link && (
                    <div className="mb-4">
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Related Link</label>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <a 
                          href={detailedData.related_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 flex items-center gap-2"
                        >
                          <LinkIcon className="w-4 h-4" />
                          <span className="break-all">{detailedData.related_link}</span>
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Attachments */}
                  {detailedData.documents && detailedData.documents.length > 0 && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Attachments</label>
                      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                        {detailedData.documents.map((doc, index) => (
                          <div key={index} className="flex items-center gap-2 text-blue-600 hover:text-blue-700">
                            <Paperclip className="w-4 h-4" />
                            <a 
                              href={`${API}/support-tickets/${detailedData.id}/attachments/${index}/download`}
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="text-sm"
                            >
                              {doc.name || `Attachment ${index + 1}`}
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Issue Actions Section */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Issue Actions</h3>
                  
                  {/* Status */}
                  <div className="mb-4">
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Status</label>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <span className={`px-3 py-1 rounded text-sm font-medium ${getStatusColor(detailedData.status || task.status)}`}>
                        {detailedData.status || task.status || 'Not set'}
                      </span>
                    </div>
                  </div>

                  {/* Assigned Team */}
                  <div className="mb-4">
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Assigned Team</label>
                    <div className="bg-gray-50 rounded-lg p-3 text-gray-700">
                      {detailedData.team_name || task.team_name || 'Not assigned'}
                    </div>
                  </div>

                  {/* Assigned Employee */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Assigned Employee</label>
                    <div className="bg-gray-50 rounded-lg p-3 text-gray-700">
                      {detailedData.assigned_employee_name || task.assigned_employee_name || 'Not assigned'}
                    </div>
                  </div>
                </div>

                {/* Priority */}
                {(detailedData.priority || task.priority) && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Priority</label>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <span className={`px-3 py-1 rounded text-sm font-medium ${getPriorityColor(detailedData.priority || task.priority)}`}>
                        {detailedData.priority || task.priority}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskPeekOverview;
